import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { MinigameStateSnapshot } from '../../shared/events';
import { Player } from '../../types';
import CartoonButton from '../CartoonButton.component';

const GAME_LABELS: Record<string, string> = {
  'closest-number': '🎯 Closest Number',
  'number-question': '🔢 Numbers',
  'pick-a-side': '⚖️ Pick a Side',
  'fastest-tap': '⚡ Fastest Tap',
  'type-the-word': '⌨️ Type the Word',
  'vote-boost': '👑 Vote Boost',
  'team-red-blue': '🔴🔵 Team Battle',
};

const DIFF_COLORS: Record<number, string> = {
  [-2]: '#4caf50', [-1]: '#8bc34a',
  [0]: 'rgba(255,255,255,0.6)',
  [1]: '#ff9800', [2]: '#f44336',
};

interface Props {
  minigame: MinigameStateSnapshot;
  players: Player[];
  isAdmin: boolean;
  onClose: () => void;
}

const MinigameResults: React.FC<Props> = ({ minigame, players, isAdmin, onClose }) => {
  const playerMap = new Map(players.map(p => [p.id, p]));
  const results = minigame.results ?? [];

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          background: 'white',
          border: '4px solid #222',
          borderRadius: '24px',
          boxShadow: '0 8px 0 #222, 0 16px 40px rgba(0,0,0,0.5)',
          minWidth: 360, maxWidth: 480, width: '92%',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #FF6F61, #6B5B95)',
            px: 3, py: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontFamily: '"Comic Sans MS", cursive, sans-serif', fontWeight: 'bold', color: 'white', fontSize: '1.1rem' }}>
            {GAME_LABELS[minigame.type]}
          </Typography>
          {minigame.targetNumber !== undefined && (
            <Typography sx={{ fontFamily: '"Comic Sans MS", cursive, sans-serif', color: '#FFD700', fontWeight: 'bold' }}>
              Target: {minigame.targetNumber}
            </Typography>
          )}
          {minigame.word && (
            <Typography sx={{ fontFamily: '"Comic Sans MS", cursive, sans-serif', color: '#FFD700', fontWeight: 'bold' }}>
              Word: {minigame.word}
            </Typography>
          )}
        </Box>

        {/* Rows */}
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {results.map((r, idx) => {
            const player = playerMap.get(r.playerId);
            if (!player) return null;
            const diffColor = DIFF_COLORS[r.coeffDiff] ?? 'rgba(255,255,255,0.6)';
            const isWinner = r.rank === 1;
            return (
              <Box
                key={r.playerId}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  px: 3, py: 1.25,
                  background: idx % 2 === 0 ? 'rgba(0,0,0,0.03)' : 'white',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <Typography sx={{ fontFamily: '"Comic Sans MS", cursive', fontWeight: 'bold', width: 28, color: isWinner ? '#FFD700' : '#555', fontSize: '0.9rem' }}>
                  #{r.rank}
                </Typography>
                {player.avatarType === 'custom' ? (
                  <Avatar src={player.avatar} sx={{ width: 32, height: 32, flexShrink: 0 }} />
                ) : (
                  <Typography sx={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>
                    {player.avatar || '🎮'}
                  </Typography>
                )}
                <Typography sx={{ fontFamily: '"Comic Sans MS", cursive', fontWeight: 'bold', flex: 1, fontSize: '0.95rem' }}>
                  {player.name}
                </Typography>
                <Typography sx={{ fontFamily: '"Comic Sans MS", cursive', color: '#555', fontSize: '0.85rem', minWidth: 70, textAlign: 'right' }}>
                  {r.scoreLabel}
                </Typography>
                <Box
                  sx={{
                    minWidth: 64, textAlign: 'center',
                    background: diffColor,
                    borderRadius: '10px',
                    px: 1, py: 0.25,
                    border: '2px solid rgba(0,0,0,0.15)',
                  }}
                >
                  <Typography sx={{ fontFamily: '"Comic Sans MS", cursive', fontWeight: 'bold', fontSize: '0.8rem', color: '#222' }}>
                    {r.coeffDiff > 0 ? `+${r.coeffDiff}` : r.coeffDiff === 0 ? '±0' : `${r.coeffDiff}`} coeff
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '2px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'center' }}>
          {isAdmin ? (
            <CartoonButton variant="contained" onClick={onClose} sx={{ background: '#ff4081' }}>
              Done →
            </CartoonButton>
          ) : (
            <Typography sx={{ fontFamily: '"Comic Sans MS", cursive', color: '#888', fontStyle: 'italic' }}>
              Waiting for admin to continue…
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MinigameResults;
